import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from 'react-router-dom'

import { filter } from 'features/dashBoard/util'
import { DashBoardConditions } from 'features/dashBoard/types'

import { SearchDetailResult } from "interface/search"
import { useListSisetu } from "interface/sisetu"
import { SisetuAnalyticsConfigure } from "../configure"
import { SisetuListSortKey } from "features/sisetu/components/list/types"

type useSisetuAnalitycsProps = {
	search: SearchDetailResult
}
export const useSisetuAnalitycs = ({
	search
}: useSisetuAnalitycsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
	const [block, setBlock] = useState<string>(searchParams.get('block') ?? 'all')	
	const [prefectureCodes, setPrefectureCodes] = useState<string[]>(searchParams.get('pref')?.split('.').filter(p => p !== '') || [])
	const [searchWord, setSearchWord] = useState<string>('')
  const [orderBy, setOrderBy] = useState<SisetuListSortKey>('total')
	const [conditions, setConditions] = useState<DashBoardConditions>({})
  const [profileId, setProfileId] = useState<number | null>(null)
  const [selectedSisetuCode, setSelectedSisetuCode] = useState<string | null>(searchParams.get('selected'))
  const [openedSisetuCode, setOpenedSisetuCode] = useState<string | null>(searchParams.get('sisetu'))

	const listState = useListSisetu(
		search.searchId,
		{ prefectureCodes, searchWord },
		{ updateTime: search.updateTime }
	)
	const { loading, loaded, error } = useMemo(
		() => {
			const loaded = (listState.total && listState.total > 0) ? (((listState.loaded ?? 0) * 100) / listState.total) : undefined
			return {
				loading: listState.loading,
				loaded,
				error: listState.error
			}
		},
		[listState]
	)
  const { sisetuList, filteredList } = useMemo(
    () => {
			const sisetuList = listState.data ?? []
			const filteredList = sisetuList.filter(
				sisetu => filter(sisetu, SisetuAnalyticsConfigure, conditions)
			) || []
			return { sisetuList, filteredList }
    },
    [listState.data, conditions]
  )
	const sortedList = useMemo(
		() => {
			const sortedList = filteredList.slice()
			sortedList.sort((a, b) => {
				switch (orderBy) {
					case 'cancer': {
						const n = (a.score?.cancer?.rank ?? 9999) - (b.score?.cancer?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'journal': {
						const n = (a.score?.journal?.rank ?? 9999) - (b.score?.journal?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'trial': {
						const n = (a.score?.trial?.rank ?? 9999) - (b.score?.trial?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'research': {
						const n = (a.score?.research?.rank ?? 9999) - (b.score?.research?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'dpc': {
						const n = (a.score?.dpc?.rank ?? 9999) - (b.score?.dpc?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'senmoni': {
						const n = (a.score?.senmoni?.rank ?? 9999) - (b.score?.senmoni?.rank ?? 9999)
						return n === 0 ? (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999) : n
					}
					case 'name': {
						return (a.sisetuNameKana ?? '').localeCompare(b.sisetuNameKana ?? '')
					}
					default: {
						return (a.score?.rank ?? 9999) - (b.score?.rank ?? 9999)
					}
				}
			})
			return sortedList
		},
		[filteredList, orderBy]
	)
  const setQueryParam = useCallback(
    (key: string, value: string | null) => setSearchParams(prev => {
      if (value && value.length > 0) {
        prev.set(key, value)
      } else {
        prev.delete(key)
      }
      return prev
    }),
    [setSearchParams]
  )
	useEffect(() => setQueryParam('block', block), [setQueryParam, block])
	useEffect(() => setQueryParam('pref', prefectureCodes.join('.')), [setQueryParam, prefectureCodes])
  useEffect(() => setQueryParam('selected', selectedSisetuCode), [setQueryParam, selectedSisetuCode])
  useEffect(() => setQueryParam('sisetu', openedSisetuCode), [setQueryParam, openedSisetuCode])

 	return useMemo(() => ({
		loading, loaded, error,
		sisetuList,
		sortedList,
		block, setBlock,
		prefectureCodes, setPrefectureCodes,
		searchWord, setSearchWord,
		orderBy, setOrderBy,
		conditions, setConditions,
		profileId, setProfileId,
		selectedSisetuCode, setSelectedSisetuCode,
		openedSisetuCode, setOpenedSisetuCode
	}), [
		loaded, loading, error,
		sisetuList,
		sortedList,
		block, setBlock,
		prefectureCodes, setPrefectureCodes,
		searchWord, setSearchWord,
		orderBy, setOrderBy,
		conditions, setConditions,
		profileId, setProfileId,
		selectedSisetuCode, setSelectedSisetuCode,
		openedSisetuCode, setOpenedSisetuCode
	])
}